// The run sheet under the Submit CTAs (DESIGN_SPEC §11.2; IMPL_ADDENDUM §1.1, §2d; RESTYLE §7): hairline rows, mono caps keys.
//   <RunSheet guest network onNetwork timeout onTimeout signedIn? showSubmitter? onShowSubmitter? onAdvanced? />
//     guest: { profile, note } — auto-detected, displayed only (pressed look, read-only); no manual override
//     network: internet (real egress through the TLS-inspecting proxy; the default) | blocked (the "simulated" mode:
//              nothing leaves the guest, a local sink answers DNS and connections and logs them). Always sent: the
//              backend's own default for a missing mode is simulated.
//     timeout: seconds, one of TIMEOUTS
//     Attribution: when signedIn is true, a checkbox lets the submitter opt in to showing their display name on the
//                  resulting job's page. Unchecked by default — submissions stay anonymous unless the viewer asks.
//     Advanced: opens a popup with the fields that are rare: command, labels, interactive input, analyze-children.
//               Hidden by default so the common submit path stays uncluttered.
import { Seg, type SegOption } from "@/components/ui";
import { WINDOWS_PROFILES, isWindowsProfile } from "@/features/submit/inferProfile";

// Shorter display labels for the two windows SKUs. Prod uses the full
// -23h2 / -22h2 IDs as the profile value sent on submit; the UI just
// shows the human-friendly "Win 11" / "Win 10".
const WINDOWS_LABELS: Record<string, string> = {
  "windows-11-23h2": "Win 11 23H2",
  "windows-10-22h2": "Win 10 22H2 LTSC",
};

export type NetworkChoice = "internet" | "simulated";

const NETWORKS: SegOption<NetworkChoice>[] = [
  { value: "internet", label: "internet", title: "Real egress through the TLS-inspecting proxy; HTTPS is decrypted and logged" },
  { value: "simulated", label: "blocked", title: "Nothing leaves the guest: a local sink answers DNS and connections and logs them (simulated mode)" },
];

export const TIMEOUTS = [30, 60, 120, 300, 1800] as const;
const TIMEOUT_LABEL: Record<number, string> = { 30: "30 s", 60: "1 min", 120: "2 min", 300: "5 min", 1800: "30 min" };

export function RunSheet({
  guest,
  onGuestProfile,
  network,
  onNetwork,
  timeout,
  onTimeout,
  signedIn,
  showSubmitter,
  onShowSubmitter,
  allowInput,
  onAllowInput,
  autoAnalyzeChildren,
  onAutoAnalyzeChildren,
  onAdvanced,
}: {
  guest: { profile: string; note: string };
  // Supplied only for Windows samples; omitted → profile stays readOnly
  // (linux-test today has nothing to switch to). When present, lets the
  // viewer flip between Win 11 and Win 10 before pressing submit.
  onGuestProfile?: (p: string) => void;
  network: NetworkChoice;
  onNetwork: (n: NetworkChoice) => void;
  timeout: number;
  onTimeout: (s: number) => void;
  signedIn?: boolean;
  showSubmitter?: boolean;
  onShowSubmitter?: (b: boolean) => void;
  // Interactive input — live mouse+keyboard forwarded to the guest.
  // Only usable when the submitter is signed in (control gates on
  // submitter_user_id == current user). When signedIn is false the row
  // still renders but reads disabled with a sign-in hint.
  allowInput?: boolean;
  onAllowInput?: (b: boolean) => void;
  // Analyze children — one-level-deep recursive analysis of files the
  // sample drops to disk, deduped by sha256.
  autoAnalyzeChildren?: boolean;
  onAutoAnalyzeChildren?: (b: boolean) => void;
  onAdvanced?: () => void;
}) {
  const timeouts: SegOption<string>[] = TIMEOUTS.map((s) => ({ value: String(s), label: TIMEOUT_LABEL[s] }));
  const isWin = isWindowsProfile(guest.profile);
  const windowsOptions: SegOption<string>[] = WINDOWS_PROFILES.map((p) => ({
    value: p,
    label: WINDOWS_LABELS[p] ?? p,
  }));
  return (
    <dl className="runsheet">
      <div>
        <dt>Guest</dt>
        <dd className="flex flex-wrap items-baseline gap-x-3">
          {isWin && onGuestProfile ? (
            <Seg
              label="Windows SKU"
              value={guest.profile}
              options={windowsOptions}
              onChange={onGuestProfile}
            />
          ) : (
            <Seg
              label="Guest profile, detected from the sample"
              readOnly
              value={guest.profile}
              options={[{ value: guest.profile, label: guest.profile }]}
            />
          )}
          <span className="meta min-w-0">{guest.note === "default" ? "auto" : guest.note}</span>
        </dd>
      </div>
      <div>
        <dt>Network</dt>
        <dd>
          <Seg label="Network" options={NETWORKS} value={network} onChange={onNetwork} />
        </dd>
      </div>
      <div>
        <dt>Timeout</dt>
        <dd>
          <Seg label="Timeout" options={timeouts} value={String(timeout)} onChange={(v) => onTimeout(Number(v))} />
        </dd>
      </div>
      {onAllowInput && (
        <div>
          <dt>Control</dt>
          <dd>
            <label className={`inline-flex items-center gap-2 text-ui ${signedIn ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}>
              <input
                type="checkbox"
                checked={signedIn ? !!allowInput : false}
                disabled={!signedIn}
                onChange={(e) => onAllowInput(e.target.checked)}
              />
              <span>
                Interactive input
                <span className="meta ml-2">
                  {signedIn
                    ? "live mouse + keyboard — only you can take control once running"
                    : "sign in to enable (control gates on your account)"}
                </span>
              </span>
            </label>
          </dd>
        </div>
      )}
      {onAutoAnalyzeChildren && (
        <div>
          <dt>Children</dt>
          <dd>
            <label className="inline-flex cursor-pointer items-center gap-2 text-ui">
              <input
                type="checkbox"
                checked={!!autoAnalyzeChildren}
                onChange={(e) => onAutoAnalyzeChildren(e.target.checked)}
              />
              <span>
                Analyze dropped files
                <span className="meta ml-2">one level deep · dedup by sha256</span>
              </span>
            </label>
          </dd>
        </div>
      )}
      {signedIn && (
        <div>
          <dt>Attribution</dt>
          <dd>
            <label className="inline-flex cursor-pointer items-center gap-2 text-ui">
              <input
                type="checkbox"
                checked={!!showSubmitter}
                onChange={(e) => onShowSubmitter?.(e.target.checked)}
              />
              <span>Show my display name on this submission</span>
            </label>
          </dd>
        </div>
      )}
      {onAdvanced && (
        <div>
          <dt>More</dt>
          <dd>
            <button type="button" className="lnk" onClick={onAdvanced}>
              Advanced options…
            </button>
          </dd>
        </div>
      )}
    </dl>
  );
}
